// src/pages/AdminScheduleEditorPage.js
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import Select from "react-select";

// ✅ Step A: removed API_BASE import
// import { API_BASE } from "../config";

import * as FaIcons from "react-icons/fa";
import * as MdIcons from "react-icons/md";
import * as FiIcons from "react-icons/fi";
import * as GiIcons from "react-icons/gi";
import * as FcIcons from "react-icons/fc"; // ✅ ADD (for FcBriefcase, FcHome, etc)

const DEFAULT_DEPARTMENT_ID = "";
const DEFAULT_WEEK_ID = 1;

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const DUTY_TYPES = [
  { value: "SHIFT", label: "Shift" },
  { value: "TRAINING", label: "Training" },

  // leaves
  { value: "DAY_OFF", label: "Day Off" },
  { value: "VACATION", label: "Vacation" },
  { value: "HOLIDAY", label: "Holiday" },
  { value: "PB", label: "PB" },
  { value: "UPB", label: "UPB" },
  { value: "LOA", label: "LOA" },
  { value: "STD", label: "STD" },
  { value: "SICK", label: "Sick" },
  { value: "NCNS", label: "NCNS" },
  { value: "FLOATER", label: "Floater" },
];

function optionFromValue(v) {
  return DUTY_TYPES.find((o) => o.value === String(v || "").toUpperCase()) || null;
}

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    borderRadius: 14,
    borderColor: state.isFocused ? "rgba(99,102,241,0.55)" : "#e5e7eb",
    boxShadow: state.isFocused
      ? "0 0 0 4px rgba(99,102,241,0.18), 0 12px 24px rgba(15,23,42,0.10)"
      : "0 8px 18px rgba(15,23,42,0.06)",
    fontWeight: 800,
    cursor: "pointer",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 12px" }),
  placeholder: (base) => ({ ...base, color: "#64748b", fontWeight: 800 }),
  singleValue: (base) => ({ ...base, color: "#0f172a", fontWeight: 900 }),
  menu: (base) => ({
    ...base,
    borderRadius: 16,
    overflow: "hidden",
    boxShadow: "0 18px 45px rgba(15,23,42,0.20)",
  }),
  menuList: (base) => ({ ...base, padding: 8 }),
  option: (base, state) => ({
    ...base,
    borderRadius: 12,
    margin: "4px 0",
    fontWeight: 800,
    backgroundColor: state.isSelected
      ? "rgba(99,102,241,0.12)"
      : state.isFocused
      ? "rgba(2,132,199,0.10)"
      : "white",
    color: "#0f172a",
    cursor: "pointer",
  }),
};

// ✅ Step A: safe JSON parsing (prevents "Unexpected token <")
async function safeJson(res) {
  if (!res) return null;

  let text = "";
  try {
    text = await res.text();
  } catch {
    return null;
  }

  const t = String(text || "").trim();
  if (!t) return null;

  try {
    return JSON.parse(t);
  } catch {
    return { error: "Non-JSON response", raw: t };
  }
}

// ✅ Robust location getter (handles string/id/object/alternate fields)
function getEntryDutyLocation(entry) {
  if (!entry) return "";

  const candidates = [
    entry.duty_location,
    entry.dutyLocation,
    entry.location,

    entry.duty_location_id,
    entry.dutyLocationId,
    entry.location_id,
    entry.locationId,

    entry.duty_location_name,
    entry.dutyLocationName,
    entry.duty_location_label,
    entry.dutyLocationLabel,
    entry.duty_location_text,
    entry.dutyLocationText,

    entry.department_name,
    entry.departmentName,
    entry.location_name,
    entry.locationName,
  ];

  for (const c of candidates) {
    if (c === null || c === undefined) continue;

    if (typeof c === "string") return c;
    if (typeof c === "number") return String(c);

    if (typeof c === "object") {
      return c.name || c.label || c.title || c.text || c.value || c.key || "";
    }
  }

  return "";
}

// ✅ Convert location to NAME (avoid saving numeric IDs like 274)
function normalizeLocationToName(departments, loc) {
  if (loc === null || loc === undefined) return null;

  // if object
  if (typeof loc === "object") {
    const maybe = loc.name || loc.label || loc.title || loc.text || loc.value;
    return normalizeLocationToName(departments, maybe);
  }

  // if number -> find department name
  if (typeof loc === "number") {
    const dep = (departments || []).find((d) => Number(d.id) === Number(loc));
    return dep?.name || String(loc); // fallback
  }

  // if string numeric -> find department name
  const s = String(loc).trim();
  if (!s) return null;

  if (/^\d+$/.test(s)) {
    const dep = (departments || []).find((d) => Number(d.id) === Number(s));
    return dep?.name || s; // fallback
  }

  // normal string location
  return s;
}

function hexToRgba(hex, alpha = 0.12) {
  if (!hex) return `rgba(100,116,139,${alpha})`;
  const h = hex.replace("#", "").trim();
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  if (full.length !== 6) return `rgba(100,116,139,${alpha})`;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function hexToRgb(hex) {
  if (!hex) return null;
  const h = String(hex).replace("#", "").trim();
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;

  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return { r, g, b };
}

// returns dark or white text depending on background color brightness
function bestTextColor(bgHex) {
  const rgb = hexToRgb(bgHex);
  if (!rgb) return "#0f172a";
  const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  return brightness > 160 ? "#0f172a" : "#ffffff";
}

function startOfWeekSunday(date) {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

// ✅ IMPORTANT: local ISO date (avoids UTC shift issues)
function toLocalISODate(d) {
  const x = new Date(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, "0");
  const day = String(x.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function dutyTypeLabel(v) {
  const key = String(v || "").toUpperCase();
  const found = DUTY_TYPES.find((x) => x.value === key);
  return found ? found.label : v || "—";
}

function isOffType(v) {
  const t = String(v || "").toUpperCase();
  return [
    "DAY_OFF",
    "VACATION",
    "HOLIDAY",
    "PB",
    "UPB",
    "LOA",
    "STD",
    "SICK",
    "NCNS",
    "FLOATER",
  ].includes(t);
}

function normalizeDutyTypeKey(v) {
  return String(v || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_"); // "DAY OFF" -> "DAY_OFF"
}

function isOffNote(note) {
  // detects notes like "VACATION", "Day Off", "DAY_OFF", etc.
  const k = normalizeDutyTypeKey(note);
  return isOffType(k);
}

// ✅ NEW: normalize date (handles "2026-01-11T00:00:00.000Z" -> "2026-01-11")
function normDate(s) {
  return String(s || "").slice(0, 10);
}

function iconFromName(iconName) {
  if (!iconName) return null;

  const name = String(iconName).trim();

  // Supports names like "FaWarehouse", "MdWork", "FiUser", "GiForklift", "FcBriefcase"
  const packs = [FaIcons, MdIcons, FiIcons, GiIcons, FcIcons]; // ✅ UPDATED

  for (const pack of packs) {
    const Comp = pack[name];
    if (Comp) return <Comp size={14} />;
  }

  return null;
}

function normKey(s) {
  return String(s || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, " ")
    .trim();
}

function getDeptColorByLocationName(departments, dutyLocation) {
  const locKey = normKey(dutyLocation);
  if (!locKey) return null;

  const getColor = (d) => d?.color || d?.department_color || d?.departmentColor || null;

  // exact match first
  const exact = (departments || []).find((d) => normKey(d?.name) === locKey);
  if (exact && getColor(exact)) return getColor(exact);

  // fallback: partial match
  const partial = (departments || []).find((d) => {
    const dk = normKey(d?.name);
    return dk && locKey.includes(dk) && getColor(d);
  });

  return partial ? getColor(partial) : null;
}

function AdminScheduleEditorPage() {
  const { authFetch } = useAuth();

  const [weeks, setWeeks] = useState([]);
  const [selectedWeekId, setSelectedWeekId] = useState(() => {
    const saved = localStorage.getItem("last_week_id");
    return saved ? Number(saved) : DEFAULT_WEEK_ID;
  });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedDepartmentId, setSelectedDepartmentId] = useState(() => {
    const saved = localStorage.getItem("last_department_id");
    return saved ? Number(saved) : DEFAULT_DEPARTMENT_ID;
  });

  const [selectedStaffId, setSelectedStaffId] = useState(() => {
    const saved = localStorage.getItem("last_staff_id");
    return saved ? saved : "all";
  });

  const [departments, setDepartments] = useState([]);
  const [currentSubduties, setCurrentSubduties] = useState([]);
  const [subdutiesError, setSubdutiesError] = useState("");

  const [editingCell, setEditingCell] = useState(null);
  const [editForm, setEditForm] = useState({
    id: null,
    duty_type: "SHIFT",
    duty_location: "",
    start_time: "",
    end_time: "",
    break_start: "",
    break_end: "",
    note: "",
    subDuty: "",
  });

  const [showCustomWeek, setShowCustomWeek] = useState(false);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const [staffSearch, setStaffSearch] = useState(() => {
    return localStorage.getItem("last_staff_search") || "";
  });

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkScope, setBulkScope] = useState("filtered");
  const [bulkSourceDay, setBulkSourceDay] = useState(1);
  const [bulkTargets, setBulkTargets] = useState([2, 3, 4, 5, 6]);
  const [bulkMode, setBulkMode] = useState("copy");
  const [bulkSetType, setBulkSetType] = useState("DAY_OFF");
  const [bulkRunning, setBulkRunning] = useState(false);

  // ✅ multi staff selection (checkboxes in grid)
  const [bulkSelectedStaffIds, setBulkSelectedStaffIds] = useState([]);

  // ✅ NICE UI POPUPS (replace alert / confirm)
  const [confirmBox, setConfirmBox] = useState(null);
  const [toast, setToast] = useState(null);

  const toastTimerRef = useRef(null);

  const showToast = useCallback((message, type = "info") => {
    setToast({ message, type });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 2600);
  }, []);

  const askConfirm = useCallback(({ title, message, okText = "OK", cancelText = "Cancel", danger = false }) => {
    return new Promise((resolve) => {
      setConfirmBox({
        title,
        message,
        okText,
        cancelText,
        danger,
        onOk: () => {
          setConfirmBox(null);
          resolve(true);
        },
        onCancel: () => {
          setConfirmBox(null);
          resolve(false);
        },
      });
    });
  }, []);

  // ✅ pick initial week once
  const didPickInitialWeekRef = useRef(false);

  // ✅ smart weeks (previous/current/next) for dropdown
  const smartWeeks = useMemo(() => {
    const today = new Date();
    const curStart = startOfWeekSunday(today);
    const curEnd = addDays(curStart, 6);

    const prevStart = addDays(curStart, -7);
    const prevEnd = addDays(prevStart, 6);

    const nextStart = addDays(curStart, 7);
    const nextEnd = addDays(nextStart, 6);

    return {
      prev: { start: toLocalISODate(prevStart), end: toLocalISODate(prevEnd) },
      cur: { start: toLocalISODate(curStart), end: toLocalISODate(curEnd) },
      next: { start: toLocalISODate(nextStart), end: toLocalISODate(nextEnd) },
    };
  }, []);

  const weekSelectValue = useMemo(() => {
    const w = weeks.find((x) => x.id === selectedWeekId);
    if (!w) return String(selectedWeekId);

    const s = normDate(w.start_date);
    const e = normDate(w.end_date);

    if (s === smartWeeks.prev.start && e === smartWeeks.prev.end) return "smart_prev";
    if (s === smartWeeks.cur.start && e === smartWeeks.cur.end) return "smart_cur";
    if (s === smartWeeks.next.start && e === smartWeeks.next.end) return "smart_next";

    return String(selectedWeekId);
  }, [weeks, selectedWeekId, smartWeeks]);

  async function findOrCreateWeekByRange(start_date, end_date) {
    const found = weeks.find((w) => normDate(w.start_date) === start_date && normDate(w.end_date) === end_date);
    if (found?.id) return found.id;

    const title = `${start_date} – ${end_date}`;

    // ✅ Step A: authFetch("/api/...") (no API_BASE)
  const res = await authFetch("/api/weeks", {
    method: "POST",
    cache: "no-store",
    body: JSON.stringify({ start_date, end_date, title, status: "draft", created_by: 1 }),
  });
    const created = await safeJson(res);
    if (!res.ok) throw new Error(created?.error || "Failed to create week");
    return created.id;
  }

  const refreshWeeks = useCallback(
    async (targetWeekId) => {
      try {
        // ✅ Step A
        const res = await authFetch("/api/weeks", { cache: "no-store" });
        const data = await safeJson(res);
        if (!res.ok) throw new Error(data?.error || "Failed to load weeks");

        setWeeks(Array.isArray(data) ? data : []);

        // ✅ NEW: if last opened week exists, open it (do NOT force current week)
        const savedId = Number(localStorage.getItem("last_week_id"));
        if (
          !didPickInitialWeekRef.current &&
          savedId &&
          (Array.isArray(data) ? data : []).some((w) => Number(w.id) === savedId)
        ) {
          setSelectedWeekId(savedId);
          didPickInitialWeekRef.current = true;
          return;
        }

        // if caller wants a specific week selected
        if (targetWeekId) {
          setSelectedWeekId(targetWeekId);
          didPickInitialWeekRef.current = true;
          return;
        }

        // ✅ fallback only if no saved week found
        if (!didPickInitialWeekRef.current && Array.isArray(data) && data.length > 0) {
          setSelectedWeekId(data[0].id);
          didPickInitialWeekRef.current = true;
        }
      } catch (err) {
        console.error("refreshWeeks error:", err);
      }
    },
    [authFetch]
  );

  useEffect(() => {
    refreshWeeks();
  }, [refreshWeeks]);

  useEffect(() => {
    async function loadDepartments() {
      try {
        const res = await authFetch("/api/my-departments", { cache: "no-store" });
        const data = await safeJson(res);
        if (!res.ok) throw new Error(data?.error || "Failed to load departments");

        const deptList = Array.isArray(data) ? data : [];
        setDepartments(deptList);

        if (!deptList.length) {
          setSelectedDepartmentId("");
          localStorage.removeItem("last_department_id");
          return;
        }

        const savedId = Number(localStorage.getItem("last_department_id"));
        const currentId = Number(selectedDepartmentId);

        const validSaved = deptList.some((d) => Number(d.id) === savedId);
        const validCurrent = deptList.some((d) => Number(d.id) === currentId);

        let nextDeptId = null;

        if (validCurrent) {
          nextDeptId = currentId;
        } else if (validSaved) {
          nextDeptId = savedId;
        } else {
          nextDeptId = Number(deptList[0].id);
        }

        setSelectedDepartmentId(nextDeptId);
        localStorage.setItem("last_department_id", String(nextDeptId));
      } catch (err) {
        console.error("loadDepartments error:", err);
      }
    }

    loadDepartments();
  }, [authFetch, selectedDepartmentId]);

  const currentWeek = weeks.find((w) => w.id === selectedWeekId);
  const isPublished = currentWeek?.status === "published";

  const ensureWeekRows = useCallback(async () => {
    if (!selectedWeekId || !selectedDepartmentId) return;
    try {
      // ✅ Step A
      const res = await authFetch("/api/schedule/ensure-week", {
        method: "POST",
        cache: "no-store",
        body: JSON.stringify({
          weekId: selectedWeekId,
          departmentId: selectedDepartmentId,
          created_by: 1,
        }),
      });
      // Don't crash UI if ensure-week returns HTML on auth failure
      const data = await safeJson(res);
      if (!res.ok) throw new Error(data?.error || "Failed to ensure week rows");
    } catch (err) {
      console.error("ensureWeekRows error:", err);
    }
  }, [authFetch, selectedWeekId, selectedDepartmentId]);

  const loadSchedule = useCallback(async () => {
    if (!selectedWeekId || !selectedDepartmentId) return;

    try {
      setLoading(true);
      setError("");

      await ensureWeekRows();

      // ✅ Step A
      const url = `/api/team-schedule?weekId=${selectedWeekId}&departmentId=${selectedDepartmentId}`;
      const res = await authFetch(url, { cache: "no-store" });
      const raw = await safeJson(res);
      if (!res.ok) throw new Error(raw?.error || `Failed to load schedule (status ${res.status})`);

      const arr = Array.isArray(raw) ? raw : raw?.rows || raw?.data || [];

      const fixed = arr.map((r) => {
        const loc =
          r.duty_location ??
          r.dutyLocation ??
          r.location ??
          r.duty_location_name ??
          r.dutyLocationName ??
          r.dutyLocationLabel ??
          r.duty_location_label ??
          r.duty_location_text ??
          r.dutyLocationText ??
          "";

        return {
          ...r,
          user_name: r.user_name ?? r.name ?? r.full_name ?? r.username ?? "",
          duty_location: typeof loc === "string" ? loc : loc?.name || "",
        };
      });

      setRows(fixed);
    } catch (err) {
      console.error("loadSchedule error:", err);
      setError(err.message || "Failed to load schedule");
    } finally {
      setLoading(false);
    }
  }, [authFetch, selectedWeekId, selectedDepartmentId, ensureWeekRows]);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  useEffect(() => {
    if (selectedWeekId) {
      localStorage.setItem("last_week_id", String(selectedWeekId));
    }
  }, [selectedWeekId]);

  // ✅ Clear bulk selection when switching week/department
  useEffect(() => {
    setBulkSelectedStaffIds([]);
  }, [selectedWeekId, selectedDepartmentId]);

  const users = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      if (!map.has(r.user_id)) {
        map.set(r.user_id, { id: r.user_id, name: r.user_name, entriesByDay: {} });
      }
      map.get(r.user_id).entriesByDay[r.day_of_week] = r;
    });
    return Array.from(map.values());
  }, [rows]);

  const filteredUsers = useMemo(() => {
    let list = users;

    if (selectedStaffId !== "all") {
      list = list.filter((u) => String(u.id) === String(selectedStaffId));
    }

    const q = String(staffSearch || "").trim().toLowerCase();
    if (q) {
      list = list.filter(
        (u) =>
          String(u.name || "").toLowerCase().includes(q) ||
          String(u.id || "").includes(q)
      );
    }

    return list;
  }, [users, selectedStaffId, staffSearch]);

  const staffOptions = useMemo(() => {
    const q = String(staffSearch || "").trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => String(u.name || "").toLowerCase().includes(q) || String(u.id).includes(q));
  }, [users, staffSearch]);

  async function copyPreviousIntoWeek(targetWeekId) {
    // ✅ Step A
    const res = await authFetch(`/api/weeks/${targetWeekId}/copy-previous`, {
      method: "POST",
      cache: "no-store",
      body: JSON.stringify({ created_by: 1 }),
    });
    const data = await safeJson(res);
    if (!res.ok) throw new Error(data?.error || "Copy previous failed");
    return data;
  }

  async function loadSubdutiesForLocation(dutyLocation) {
    try {
      setSubdutiesError("");
      setCurrentSubduties([]);

      if (!dutyLocation) return;

      const byName = departments.find(
        (d) => String(d.name).toUpperCase() === String(dutyLocation).toUpperCase()
      );

      const departmentIds = byName ? [byName.id] : [];
      if (departmentIds.length === 0) return;

      let all = [];
      for (const depId of departmentIds) {
        // ✅ Step A
        const res = await authFetch(`/api/departments/${depId}/subduties`, { cache: "no-store" });
        const data = await safeJson(res);
        if (!res.ok) throw new Error(data?.error || `Failed to load sub-duties (status ${res.status})`);
        all = all.concat(data || []);
      }

      const unique = [];
      const seen = new Set();
      for (const sd of all) {
        if (!seen.has(sd.name)) {
          seen.add(sd.name);
          unique.push(sd);
        }
      }

      setCurrentSubduties(unique);

      setEditForm((prev) => {
        const currentSub = String(prev.subDuty || "").trim();
        const match = unique.find((sd) => String(sd.name || "").trim() === currentSub);

        return {
          ...prev,
          subDuty: match ? match.name : prev.subDuty,
        };
      });
    } catch (err) {
      console.error("loadSubdutiesForLocation error:", err);
      setSubdutiesError(err.message || "Failed to load sub-duties");
    }
  }

  function handleOpenEdit(userId, dayIndex) {
    const userRow = users.find((u) => u.id === userId);
    if (!userRow) return;

    const entry = userRow.entriesByDay[dayIndex];

    if (!entry?.id) {
      alert("Missing schedule entry. Click refresh or re-open this week.");
      return;
    }

    setEditingCell({ userId, dayIndex, entry });

    setEditForm({
      id: entry.id,
      duty_type: entry.duty_type || "SHIFT",
      duty_location: entry.duty_location || "",
      start_time: entry.start_time || "",
      end_time: entry.end_time || "",
      break_start: entry.break_start || "",
      break_end: entry.break_end || "",
      note: entry.note || "",
      subDuty: entry.sub_duty || entry.subDuty || "",
    });

    loadSubdutiesForLocation(getEntryDutyLocation(entry));
  }

  function updateEditField(field, value) {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  }
  function handleDutyLocationChange(value) {
    const name = normalizeLocationToName(departments, value);
    setEditForm((prev) => ({ ...prev, duty_location: name || "" }));
    loadSubdutiesForLocation(name);
  }
  function handleSubDutyChange(value) {
    setEditForm((prev) => ({ ...prev, subDuty: value, note: value || prev.note }));
  }

  async function handleQuickDutyTypeChange(entry, newType) {
    if (!entry?.id) return;

    if (isPublished) {
      alert("This week is published (locked). Unpublish to edit.");
      return;
    }

    const off = isOffType(newType);
    const shiftLike = newType === "SHIFT" || newType === "TRAINING";

    const nextLocation = off ? null : entry.duty_location || (shiftLike ? "CONTROL ROOM" : null);

    const prevNote = String(entry.note || "").trim();
    const wasOffNote = isOffNote(prevNote);

    // ✅ ALSO check subDuty
    const prevSub = String(entry.sub_duty || entry.subDuty || "").trim();
    const wasOffSub = isOffNote(prevSub);

    const payload = {
      duty_type: newType,
      duty_location: off ? null : normalizeLocationToName(departments, nextLocation),
      start_time: off ? null : entry.start_time,
      end_time: off ? null : entry.end_time,
      break_start: off ? null : entry.break_start,
      break_end: off ? null : entry.break_end,

      // ✅ note rule
      note: off ? newType.replace("_", " ") : wasOffNote ? null : entry.note,

      // ✅ sub_duty rule (THIS FIXES YOUR BUG)
      sub_duty: off ? null : wasOffSub ? null : entry.sub_duty || entry.subDuty || null,

      last_updated_by: 1,
    };

    const normalizeRows = (arr) =>
      (Array.isArray(arr) ? arr : []).map((r) => {
        const loc =
          r.duty_location ??
          r.dutyLocation ??
          r.location ??
          r.duty_location_name ??
          r.dutyLocationName ??
          r.dutyLocationLabel ??
          r.duty_location_label ??
          r.duty_location_text ??
          r.dutyLocationText ??
          "";

        return {
          ...r,
          user_name: r.user_name ?? r.name ?? r.full_name ?? r.username ?? "",
          duty_location: typeof loc === "string" ? loc : loc?.name || "",
        };
      });

    try {
      // ✅ Step A
      const res = await authFetch(`/api/schedule/${entry.id}`, {
        method: "PUT",
        cache: "no-store",
        body: JSON.stringify(payload),
      });

      const data = await safeJson(res);
      if (!res.ok) throw new Error(data?.error || "Update failed");

      if (Array.isArray(data)) {
        setRows(normalizeRows(data));
        return;
      }
      if (Array.isArray(data?.rows)) {
        setRows(normalizeRows(data.rows));
        return;
      }
      if (Array.isArray(data?.data)) {
        setRows(normalizeRows(data.data));
        return;
      }

      const updatedRow = data?.row || data?.updatedRow;
      if (updatedRow?.id) {
        const normalized = normalizeRows([updatedRow])[0];
        setRows((prev) => prev.map((r) => (r.id === normalized.id ? { ...r, ...normalized } : r)));
        return;
      }

      setRows((prev) => prev.map((r) => (r.id === entry.id ? { ...r, ...payload } : r)));
    } catch (err) {
      alert(err.message);
    }
  }

  async function handleCreateNextWeekAuto() {
    const today = new Date();
    const thisWeekStart = startOfWeekSunday(today);
    const nextWeekStart = addDays(thisWeekStart, 7);
    const nextWeekEnd = addDays(nextWeekStart, 6);

    const start_date = toLocalISODate(nextWeekStart);
    const end_date = toLocalISODate(nextWeekEnd);
    const title = `${start_date} – ${end_date}`;

    try {
      // ✅ Step A
      const res = await authFetch("/api/weeks", {
        method: "POST",
        cache: "no-store",
        body: JSON.stringify({
        start_date,
        end_date,
          title,
          status: "draft",
          created_by: 1,
        }),
      });

      const created = await safeJson(res);
      if (!res.ok) throw new Error(created?.error || "Failed to create next week");

      try {
        await copyPreviousIntoWeek(created.id);
      } catch (copyErr) {
        console.warn("copy-previous failed:", copyErr);
        alert(`Week created, but copy previous failed: ${copyErr.message}`);
      }

      await refreshWeeks(created.id);
      await loadSchedule();
    } catch (err) {
      console.error("handleCreateNextWeekAuto error:", err);
      alert(err.message || "Failed to create next week");
    }
  }

  async function handleCreateCustomWeek() {
    if (!customStart || !customEnd) {
      alert("Please select start date and end date.");
      return;
    }

    try {
      // ✅ NEW: if already exists, just open it
      const existing = weeks.find((w) => normDate(w.start_date) === customStart && normDate(w.end_date) === customEnd);

      if (existing?.id) {
        setShowCustomWeek(false);
        setCustomStart("");
        setCustomEnd("");
        await refreshWeeks(existing.id);
        await loadSchedule();
        return;
      }

      // ✅ otherwise create new
      const title = `${customStart} – ${customEnd}`;

      // ✅ Step A
      const res = await authFetch("/api/weeks", {
        method: "POST",
        body: JSON.stringify({
          start_date: customStart,
          end_date: customEnd,
          title,
          status: "draft",
          created_by: 1,
        }),
      });

      const created = await safeJson(res);
      if (!res.ok) throw new Error(created?.error || "Failed to create custom week");

      try {
        await copyPreviousIntoWeek(created.id);
      } catch (copyErr) {
        console.warn("copy-previous failed:", copyErr);
        alert(`Week created, but copy previous failed: ${copyErr.message}`);
      }

      setShowCustomWeek(false);
      setCustomStart("");
      setCustomEnd("");

      await refreshWeeks(created.id);
      await loadSchedule();
    } catch (err) {
      console.error("handleCreateCustomWeek error:", err);
      alert(err.message || "Failed to create custom week");
    }
  }

  async function handleCloneWeek() {
    if (!selectedWeekId) return alert("No week selected to clone.");

    const sourceWeek = weeks.find((w) => w.id === selectedWeekId);
    if (!sourceWeek) return;

    setShowCustomWeek(true);
    setCustomStart(sourceWeek.start_date || "");
    setCustomEnd(sourceWeek.end_date || "");
    alert("Choose new start/end dates below, then click Create.");
  }

  async function handleCopyPreviousWeek() {
    if (!selectedWeekId) return alert("No week selected.");

    if (isPublished) {
      alert("This week is published (locked). Unpublish it first, then copy.");
      return;
    }

    const ok = await askConfirm({
      title: "Copy previous week?",
      message: "Copy previous week schedule into this week? This will overwrite current entries.",
      okText: "Copy",
      cancelText: "Cancel",
      danger: true,
    });
    if (!ok) return;

    try {
      // ✅ Step A
      const res = await authFetch(`/api/weeks/${selectedWeekId}/copy-previous`, {
        method: "POST",
        cache: "no-store",
        body: JSON.stringify({ created_by: 1 }),
      });

      const data = await safeJson(res);
      if (!res.ok) throw new Error(data?.error || "Copy previous week failed");

      await refreshWeeks(selectedWeekId);
      await loadSchedule();

      showToast(`Copied from: ${data.sourceTitle || `Week ${data.sourceWeekId}`}`, "success");
    } catch (err) {
      console.error("handleCopyPreviousWeek error:", err);
      showToast(err.message || "Failed to copy previous week", "error");
    }
  }

  async function handleTogglePublishWeek() {
    if (!selectedWeekId) return alert("No week selected.");

    const isPub = currentWeek?.status === "published";
    const nextStatus = isPub ? "draft" : "published";

    const ok = await askConfirm({
      title: isPub ? "Unpublish week?" : "Publish week?",
      message: isPub
        ? "This will unlock the week so you can edit."
        : "This will lock the week so staff schedule cannot be edited.",
      okText: isPub ? "Unpublish" : "Publish",
      cancelText: "Cancel",
      danger: !isPub, // publish is the “strong” action
    });
    if (!ok) return;

    try {
      // ✅ Step A
      const res = await authFetch(`/api/weeks/${selectedWeekId}/status`, {
        method: "PATCH",
        cache: "no-store",
        body: JSON.stringify({ status: nextStatus }),
      });

      const data = await safeJson(res);
      if (!res.ok) throw new Error(data?.error || "Failed to change status");

      await refreshWeeks(selectedWeekId);
      await loadSchedule();
      showToast(isPub ? "Week unpublished (editable)" : "Week published (locked)", "success");
    } catch (err) {
      console.error("handleTogglePublishWeek error:", err);
      showToast(err.message || "Failed to change publish status", "error");
    }
  }

  async function handleSaveEdit(e) {
    e.preventDefault();
    if (!editingCell || !editForm.id) {
      setEditingCell(null);
      return;
    }

    if (isPublished) {
      alert("This week is published (locked). Unpublish to edit.");
      return;
    }

    const dtKey = normalizeDutyTypeKey(editForm.duty_type);
    const off = isOffType(dtKey);

    // If switching to SHIFT/TRAINING and note is an old off-note like "VACATION", clear it.
    const rawNote = editForm.note ?? "";
    const nextNote = off
      ? rawNote.trim()
        ? rawNote
        : dtKey.replace("_", " ")
      : isOffNote(rawNote)
      ? null
      : rawNote.trim()
      ? rawNote
      : null;

    const rawSub = editForm.subDuty ?? "";
    const nextSubDuty = off ? null : isOffNote(rawSub) ? null : rawSub.trim() ? rawSub : null;

    const payload = {
      duty_type: dtKey,
      duty_location: off ? null : normalizeLocationToName(departments, editForm.duty_location),
      start_time: off ? null : editForm.start_time || null,
      end_time: off ? null : editForm.end_time || null,
      break_start: off ? null : editForm.break_start || null,
      break_end: off ? null : editForm.break_end || null,
      note: nextNote,
      sub_duty: nextSubDuty,
      last_updated_by: 1,
    };

    try {
      // ✅ Step A
      const res = await authFetch(`/api/schedule/${editForm.id}`, {
        method: "PUT",
        cache: "no-store",
        body: JSON.stringify(payload), // ✅ FIX
      });

      const data = await safeJson(res);
      if (!res.ok) throw new Error(data?.error || "Failed to save schedule entry");

      setRows((prev) => prev.map((r) => (r.id === editForm.id ? { ...r, ...payload } : r)));
      setEditingCell(null);
    } catch (err) {
      console.error("handleSaveEdit error:", err);
      alert(err.message || "Failed to save");
    }
  }

  // ✅ checkbox helpers
  const visibleUserIds = useMemo(() => filteredUsers.map((u) => u.id), [filteredUsers]);

  const allVisibleChecked = useMemo(() => {
    if (visibleUserIds.length === 0) return false;
    return visibleUserIds.every((id) => bulkSelectedStaffIds.includes(id));
  }, [visibleUserIds, bulkSelectedStaffIds]);

  const someVisibleChecked = useMemo(() => {
    if (visibleUserIds.length === 0) return false;
    const count = visibleUserIds.filter((id) => bulkSelectedStaffIds.includes(id)).length;
    return count > 0 && count < visibleUserIds.length;
  }, [visibleUserIds, bulkSelectedStaffIds]);

  function toggleBulkStaff(id, checked) {
    setBulkSelectedStaffIds((prev) => {
      if (checked) return Array.from(new Set([...prev, id]));
      return prev.filter((x) => x !== id);
    });
  }

  function toggleAllVisible(checked) {
    setBulkSelectedStaffIds((prev) => {
      if (checked) return Array.from(new Set([...prev, ...visibleUserIds]));
      const vis = new Set(visibleUserIds);
      return prev.filter((id) => !vis.has(id));
    });
  }

  function clearBulkSelected() {
    setBulkSelectedStaffIds([]);
  }

  function clearScheduleFilters() {
  setSelectedStaffId("all");
  setStaffSearch("");
  localStorage.setItem("last_staff_id", "all");
  localStorage.setItem("last_staff_search", "");
}

  const hasActiveScheduleFilters =
    selectedStaffId !== "all" || String(staffSearch || "").trim() !== "";

  function getBulkUsers() {
    if (bulkScope === "selected") {
      if (bulkSelectedStaffIds.length > 0) {
        const set = new Set(bulkSelectedStaffIds.map(String));
        return users.filter((u) => set.has(String(u.id)));
      }
      if (selectedStaffId !== "all") {
        return users.filter((u) => String(u.id) === String(selectedStaffId));
      }
      return [];
    }
    return filteredUsers;
  }

  function buildPayloadFromEntry(entry) {
    const dt = (entry?.duty_type || "DAY_OFF").toUpperCase();
    const off = isOffType(dt);

    const rawNote = entry?.note ?? null;
    const rawSub = entry?.sub_duty ?? entry?.subDuty ?? null;

    // ✅ If copying SHIFT/TRAINING, do NOT copy old off-notes in note/sub_duty
    const cleanedNote = !off && isOffNote(rawNote) ? null : rawNote;
    const cleanedSub = !off && isOffNote(rawSub) ? null : rawSub;

    return {
      duty_type: dt,
      duty_location: off ? null : normalizeLocationToName(departments, entry?.duty_location),
      start_time: off ? null : entry?.start_time || null,
      end_time: off ? null : entry?.end_time || null,
      break_start: off ? null : entry?.break_start || null,
      break_end: off ? null : entry?.break_end || null,
      note: off ? rawNote || dt.replace("_", " ") : cleanedNote || null,

      // ✅ ADD THIS
      sub_duty: off ? null : cleanedSub || null,

      last_updated_by: 1,
    };
  }

  async function bulkApply() {
    if (isPublished) {
      showToast("This week is published (locked). Unpublish to edit bulk actions.", "warn");
      return;
    }

    const targetUsers = getBulkUsers();
    if (!targetUsers.length) {
      showToast("Select staff using the checkboxes (or choose a staff in the Staff dropdown).", "warn");
      return;
    }

    if (!bulkTargets.length) {
      showToast("Select at least one target day.", "warn");
      return;
    }

    const ok = await askConfirm({
      title: "Confirm bulk action",
      message: `Apply bulk changes to ${targetUsers.length} staff and ${bulkTargets.length} day(s)?`,
      okText: "Apply",
      cancelText: "Cancel",
      danger: true,
    });
    if (!ok) return;

    try {
      setBulkRunning(true);

      const updates = [];

      for (const u of targetUsers) {
        const sourceEntry = u.entriesByDay[bulkSourceDay];

        if (bulkMode === "copy") {
          if (!sourceEntry?.id) continue;
          const sourcePayload = buildPayloadFromEntry(sourceEntry);

          for (const dayIdx of bulkTargets) {
            const targetEntry = u.entriesByDay[dayIdx];
            if (!targetEntry?.id) continue;

            updates.push(
              (async () => {
                const res = await authFetch(`/api/schedule/${targetEntry.id}`, {
                  method: "PUT",
                  cache: "no-store",
                  body: JSON.stringify(sourcePayload), // ✅ FIXED
                });
                const data = await safeJson(res);
                if (!res.ok) throw new Error(data?.error || "Bulk update failed");
                return { id: targetEntry.id, payload: sourcePayload }; // ✅ FIXED
              })()
            );
          }
        } else {
          for (const dayIdx of bulkTargets) {
            const targetEntry = u.entriesByDay[dayIdx];
            if (!targetEntry?.id) continue;

            const off = isOffType(bulkSetType);
            const payload = {
              duty_type: bulkSetType,
              duty_location: off ? null : normalizeLocationToName(departments, targetEntry.duty_location),
              start_time: off ? null : targetEntry.start_time,
              end_time: off ? null : targetEntry.end_time,
              break_start: off ? null : targetEntry.break_start,
              break_end: off ? null : targetEntry.break_end,
              note: off ? bulkSetType.replace("_", " ") : targetEntry.note,

              sub_duty: off
                ? null
                : isOffNote(targetEntry.sub_duty || targetEntry.subDuty)
                ? null
                : targetEntry.sub_duty || targetEntry.subDuty || null,

              last_updated_by: 1,
            };

            updates.push(
              (async () => {
                // ✅ Step A
                const res = await authFetch(`/api/schedule/${targetEntry.id}`, {
                  method: "PUT",
                  body: JSON.stringify(payload),
                });
                const data = await safeJson(res);
                if (!res.ok) throw new Error(data?.error || "Bulk update failed");
                return { id: targetEntry.id, payload };
              })()
            );
          }
        }
      }

      const results = await Promise.all(updates);

      setRows((prev) => {
        const byId = new Map(results.map((r) => [r.id, r.payload]));
        return prev.map((r) => (byId.has(r.id) ? { ...r, ...byId.get(r.id) } : r));
      });

      showToast(`Bulk action completed. Updated ${results.length} entries.`, "success");
    } catch (e) {
      showToast(e.message || "Bulk action failed", "error");
    } finally {
      setBulkRunning(false);
    }
  }

  function renderCellSummary(entry) {
    if (!entry) return <span style={{ color: "#999" }}>—</span>;

    const dt = (entry.duty_type || "DAY_OFF").toUpperCase();

    if (isOffType(dt)) {
      return (
        <div className="cell-summary off-center">
          <div className={`badge badge-${dt}`}>{dutyTypeLabel(dt)}</div>
        </div>
      );
    }

    const time = entry.start_time && entry.end_time ? `${entry.start_time}–${entry.end_time}` : "No time set";

    const rawLoc = getEntryDutyLocation(entry);
    const locText = rawLoc ? String(rawLoc).replace(/_/g, " ").trim() : "No location";
    const subText = String(entry?.sub_duty || entry?.subDuty || "").replace(/_/g, " ").trim();
    const locWithSub = subText ? `${locText} • ${subText}` : locText;
    const dept = (departments || []).find((d) => normKey(d?.name) === normKey(locText));

    const deptIconName = dept?.icon || dept?.icon_name || dept?.iconName || null;
    const iconEl = iconFromName(deptIconName) || (dept?.emoji ? <span>{dept.emoji}</span> : null);

    const deptColor = dept?.color || dept?.department_color || dept?.departmentColor || "#94a3b8";
    const pillText = bestTextColor(deptColor);

    return (
      <div className="cell-summary">
        <div className="time">{time}</div>
        <div className="cell-loc" title={locWithSub}>
          <span className="cell-loc-icon" style={{ color: pillText }}>
            {iconEl || "📍"}
          </span>
          <span
            className="cell-loc-pill"
            style={{
              background: hexToRgba(deptColor, 0.18),
              border: `1px solid ${hexToRgba(deptColor, 0.30)}`,
              color: pillText,
            }}
          >
            <span
              className="dot"
              style={{
                background: deptColor,
                boxShadow: `0 0 0 4px ${hexToRgba(deptColor, 0.18)}`,
              }}
            />
            {locWithSub}
          </span>
        </div>
      </div>
    );
  }

  if (loading && !rows.length) return <p style={{ padding: "20px" }}>Loading schedule...</p>;
  if (error)
    return (
      <div style={{ padding: "20px" }}>
        <h1>Admin Schedule Editor</h1>
        <p style={{ color: "red" }}>{error}</p>
      </div>
    );

  return (
    <div className={`page ${isPublished ? "locked" : ""}`}>
      <style>{`
        .page{
          padding: 22px 24px 32px;
          background:#f4f6fb;
          min-height: calc(100vh - 60px);
          color:#0f172a;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        }
        .header{
          display:flex;
          align-items:flex-start;
          justify-content:space-between;
          gap:16px;
          margin-bottom:16px;
        }
        .header h1{
          margin:0;
          font-size:28px;
          letter-spacing:-0.02em;
        }
        .sub{
          margin:6px 0 0;
          color:#64748b;
          font-size:13px;
        }
        .status-pill{
          display:inline-flex;
          align-items:center;
          gap:8px;
          padding: 8px 12px;
          border-radius: 999px;
          background:#fff;
          border:1px solid #eef2f7;
          box-shadow: 0 10px 22px rgba(15,23,42,0.06);
          font-weight:900;
          font-size:12px;
          white-space:nowrap;
        }
        .dot-mini{
          width:10px; height:10px;
          border-radius:999px;
          background:#16a34a;
          box-shadow: 0 0 0 4px rgba(22,163,74,.12);
        }
        .locked .dot-mini{
          background:#b45309;
          box-shadow: 0 0 0 4px rgba(180,83,9,.12);
        }

        .list-card{
          width: 100%;
          background:#fff;
          border-radius: 22px;
          padding: 14px 14px 10px;
          box-shadow: 0 18px 45px rgba(15,23,42,0.08);
          border: 1px solid #eef2f7;
        }

        .toolbar{
          display:flex;
          flex-wrap:wrap;
          gap:12px;
          align-items:center;
          justify-content:space-between;
        }

        .filters{
          display:flex;
          flex-wrap:wrap;
          gap:10px;
          align-items:center;
        }
        .control{
          display:flex;
          align-items:center;
          gap:10px;
          padding: 10px 12px;
          background:#f8fafc;
          border: 1px solid #eef2f7;
          border-radius: 16px;
        }
        .control label{
          font-weight:900;
          font-size:13px;
          color:#0f172a;
          display:flex;
          align-items:center;
          gap:8px;
        }
        .control select, .control input[type="text"], .control input[type="date"]{
          border:1px solid #e5e7eb;
          border-radius: 999px;
          padding: 8px 10px;
          background:#fff;
          outline:none;
          font-size:13px;
        }
        .control select{
          padding-right: 34px;
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
        }
        .control input[type="text"]{ border-radius: 12px; }

        .select-wrap{
          position: relative;
          display:inline-flex;
          align-items:center;
        }
        .select-wrap:after{
          content:"▾";
          position:absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          color:#64748b;
          font-size: 12px;
          pointer-events:none;
        }

        .actions{
          display:flex;
          flex-wrap:wrap;
          gap:8px;
          align-items:center;
          justify-content:flex-end;
        }
                  .clear-filter-btn{
          border:1px solid #e2e8f0;
          background: linear-gradient(180deg, #ffffff, #f8fafc);
          border-radius: 14px;
          padding: 10px 14px;
          font-weight:900;
          cursor:pointer;
          font-size:13px;
          color:#0f172a;
          box-shadow: 0 1px 2px rgba(15,23,42,0.04);
          transition: transform .14s ease, filter .14s ease, background .14s ease, border-color .14s ease;
        }
        .clear-filter-btn:hover{
          transform: translateY(-1px);
          filter: brightness(1.01);
          background:#fff;
          border-color:#cbd5e1;
        }

        .btn{
          border:1px solid #e5e7eb;
          background:#fff;
          border-radius:999px;
          padding: 10px 14px;
          font-weight:900;
          cursor:pointer;
          transition: transform .14s ease, filter .14s ease, background .14s ease;
          font-size:13px;
        }
        .btn:hover{ transform: translateY(-1px); filter: brightness(1.02); }
        .btn:active{ transform: translateY(0); }
        .btn-primary{
          border:none;
          color:#0b1120;
          background: linear-gradient(135deg, #38bdf8, #6366f1);
          box-shadow: 0 14px 28px rgba(15,23,42,0.14);
        }
        .btn-danger{
          border:none;
          background:#dc2626;
          color:#fff;
        }
        .btn-ghost{
          border:1px solid rgba(37,99,235,0.18);
          background: linear-gradient(135deg, #eef2ff, #e0f2fe);
          color:#0f172a;
        }
        .btn:disabled{ opacity:.55; cursor:not-allowed; transform:none; }

        .locked .control,
        .locked .grid-card{
          opacity: .95;
        }
        .locked .locked-hint{
          display:flex;
          align-items:center;
          gap:8px;
          padding: 8px 12px;
          background:#fff7ed;
          border: 1px solid rgba(180,83,9,.20);
          border-radius: 14px;
          color:#92400e;
          font-weight:800;
          font-size:12px;
        }

        .meta-row{
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:12px;
          padding: 12px 4px 2px;
          color:#64748b;
          font-size:12px;
        }
        .meta-row b{ color:#0f172a; }

        .grid-card{
          margin-top: 12px;
          width: 100%;
          background:#fff;
          border-radius: 22px;
          padding: 14px;
          box-shadow: 0 18px 45px rgba(15,23,42,0.08);
          border: 1px solid #eef2f7;
        }
        .grid-head{
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:10px;
          padding: 6px 4px 12px;
          border-bottom: 1px solid #eef2f7;
          margin-bottom: 12px;
        }
        .grid-head h3{
          margin:0;
          font-size:16px;
          font-weight:900;
        }

        .table-wrap{
          overflow:auto;
          border-radius: 16px;
          border: 1px solid #eef2f7;
          background:#fff;
        }

        table{
          width:100%;
          min-width: 1100px;
          border-collapse: separate;
          border-spacing: 0;
          font-size: 13px;
        }
        thead th{
          position: sticky;
          top: 0;
          z-index: 3;
          text-align:left;
          padding: 12px 10px;
          color:#334155;
          background:#f8fafc;
          border-bottom:1px solid #eef2f7;
          font-weight:900;
          white-space: nowrap;
        }

        th.sticky-col, td.sticky-col{
          position: sticky;
          left: 0;
          z-index: 4;
          background: #fff;
          border-right: 1px solid #eef2f7;
        }
        thead th.sticky-col{
          z-index: 6;
          background:#f8fafc;
        }

        tbody td{
          padding: 10px;
          border-bottom:1px solid #eef2f7;
          vertical-align: top;
          background:#fff;
        }
        tbody tr:hover td{ background:#fbfdff; }
        tbody tr:hover td.sticky-col{ background:#fbfdff; }

        .day-cell{ min-width: 180px; }
        .cell-box{
          height: 168px;
          display:flex;
          flex-direction:column;
          gap:8px;
          padding: 10px;
          border: 1px solid #eef2f7;
          border-radius: 16px;
          background: #fff;
          box-shadow: 0 10px 18px rgba(15,23,42,0.04);
        }
        .cell-top{
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:8px;
        }
        .cell-top select{
          width: 100%;
          border:1px solid #e5e7eb;
          border-radius: 12px;
          padding: 8px 10px;
          font-weight:800;
          background:#fff;
          outline:none;
        }
        .cell-body{
          flex: 1 1 auto;
          overflow: visible;
        }
        .cell-footer{
          display:flex;
          justify-content:flex-end;
        }

        .small-btn{
          border:1px solid #e5e7eb;
          background:#f1f5ff;
          border-radius: 12px;
          padding: 8px 10px;
          font-weight:900;
          cursor:pointer;
          font-size:12px;
        }

        .staff-card{
          display:flex;
          align-items:flex-start;
          gap:10px;
          min-width: 240px;
        }
        .staff-info{
          display:flex;
          flex-direction:column;
          gap:4px;
        }
        .staff-name{ font-weight:900; color:#0f172a; }
        .staff-sub{ font-size:12px; color:#64748b; }

        .rowchk{
          width:16px;
          height:16px;
          margin-top: 2px;
          accent-color: #6366f1;
          cursor:pointer;
        }

        .cell-summary{ font-size:12px; color:#0f172a; }
        .off-center{
          height: 100%;
          display:flex;
          align-items:center;
          justify-content:center;
        }
        .badge{
          display:inline-flex;
          align-items:center;
          gap:8px;
          padding: 6px 12px;
          border-radius: 999px;
          border: 1px solid #eef2f7;
          background:#f8fafc;
          font-weight:900;
          width: fit-content;
        }
        .badge-DAY_OFF{ background:#f1f5f9; border-color: rgba(100,116,139,.25); }
        .badge-VACATION{ background:#ecfeff; border-color: rgba(6,182,212,.25); }

        .time{ font-weight:900; margin-bottom: 8px; }
        .cell-loc{ display:flex; align-items:center; gap:8px; }
        .cell-loc-icon{ width:16px; display:inline-flex; justify-content:center; }
        .cell-loc-pill{
          display:inline-flex;
          align-items:center;
          gap:8px;
          padding: 6px 10px;
          border-radius: 999px;
          border: 1px solid #eef2f7;
          background:#fff;
          max-width: 100%;
          overflow:hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .dot{ width:8px; height:8px; border-radius:999px; }

        .modal-overlay{
          position:fixed; inset:0;
          background: rgba(2,6,23,0.45);
          display:flex;
          align-items:center;
          justify-content:center;
          padding: 24px;
          z-index: 50;
        }
        .modal{
          width: min(860px, 96vw);
          background:#fff;
          border-radius: 18px;
          box-shadow: 0 28px 70px rgba(15,23,42,0.35);
          overflow:hidden;
        }
        .modal-header{
          display:flex;
          align-items:center;
          justify-content:space-between;
          padding: 14px 16px;
          border-bottom: 1px solid #eef2f7;
        }
        .modal-header h4{ margin:0; font-size:16px; font-weight:900; }
        .x-btn{ border:none; background:transparent; font-size:20px; cursor:pointer; color:#64748b; }
        .modal-body{
          padding: 16px;
          display:grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        .field label{
          display:block;
          font-size:12px;
          color:#334155;
          font-weight:800;
          margin-bottom:6px;
        }
        .field input, .field select{
          width:100%;
          border:1px solid #e5e7eb;
          border-radius: 12px;
          padding: 10px 12px;
          background:#fff;
          outline:none;
        }
        .span2{ grid-column: 1 / -1; }
        .meta-mini{
          margin-top: 6px;
          display:flex;
          align-items:center;
          gap:8px;
          font-size:12px;
          color:#64748b;
        }
        .modal-footer{
          padding: 14px 16px;
          border-top: 1px solid #eef2f7;
          display:flex;
          justify-content:flex-end;
          gap:10px;
        }
        .bulk-panel{
          margin-top: 10px;
          padding: 12px;
          border-radius: 16px;
          background:#f8fafc;
          border: 1px solid #eef2f7;
          display:flex;
          flex-wrap:wrap;
          gap:10px;
          align-items:center;
        }
        .bulk-title{ font-weight:900; margin-right: 4px; }
        .bulk-days{ display:flex; flex-wrap:wrap; gap:8px; align-items:center; }
        .chk{
          display:inline-flex;
          align-items:center;
          gap:6px;
          padding: 6px 10px;
          border-radius: 999px;
          background:#fff;
          border: 1px solid #e5e7eb;
          font-size:12px;
          font-weight:800;
          cursor:pointer;
          user-select:none;
        }
        .chk input{ margin:0; }

        .bulk-mini{
          font-size:12px;
          color:#64748b;
          font-weight:800;
          margin-left: 2px;
        }

        .confirm-overlay{
          position:fixed; inset:0;
          background: rgba(2,6,23,0.45);
          display:flex;
          align-items:center;
          justify-content:center;
          padding: 24px;
          z-index: 80;
        }
        .confirm-modal{
          width: min(520px, 96vw);
          background:#fff;
          border-radius: 18px;
          box-shadow: 0 28px 70px rgba(15,23,42,0.35);
          overflow:hidden;
          border: 1px solid #eef2f7;
        }
        .confirm-head{
          padding: 14px 16px;
          border-bottom: 1px solid #eef2f7;
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap:10px;
        }
        .confirm-title{
          margin:0;
          font-size:16px;
          font-weight: 950;
        }
        .confirm-body{
          padding: 14px 16px 4px;
          color:#334155;
          font-size: 13px;
          font-weight: 700;
          line-height: 1.45;
        }
        .confirm-actions{
          padding: 14px 16px;
          display:flex;
          justify-content:flex-end;
          gap:10px;
          border-top: 1px solid #eef2f7;
          background:#fff;
        }
        .btn-danger-soft{
          border:none;
          color:#fff;
          background: linear-gradient(135deg, #ef4444, #b91c1c);
          box-shadow: 0 14px 28px rgba(239,68,68,0.22);
        }

        .toast{
          position: fixed;
          right: 18px;
          bottom: 18px;
          z-index: 90;
          width: min(360px, calc(100vw - 36px));
          border-radius: 16px;
          padding: 12px 14px;
          background:#fff;
          border: 1px solid #eef2f7;
          box-shadow: 0 18px 45px rgba(15,23,42,0.18);
          display:flex;
          gap:10px;
          align-items:flex-start;
          font-weight: 850;
          color:#0f172a;
        }
        .toast .mini-dot{
          width:10px; height:10px;
          border-radius:999px;
          margin-top: 4px;
          background:#6366f1;
          box-shadow: 0 0 0 4px rgba(99,102,241,.12);
          flex: 0 0 auto;
        }
        .toast.success .mini-dot{ background:#16a34a; box-shadow: 0 0 0 4px rgba(22,163,74,.12); }
        .toast.error .mini-dot{ background:#ef4444; box-shadow: 0 0 0 4px rgba(239,68,68,.12); }
        .toast.warn .mini-dot{ background:#b45309; box-shadow: 0 0 0 4px rgba(180,83,9,.12); }

        @media (max-width: 900px){
          .modal-body{ grid-template-columns: 1fr; }
          .control{ width: 100%; justify-content:space-between; }
          .actions{ width: 100%; justify-content:flex-start; }

        }
          @media (max-width: 640px){
          /* Page spacing */
          .page{
            padding: 14px 12px 20px;
          }

          /* Header stacks */
          .header{
            flex-wrap: wrap;
            gap: 10px;
          }
          .status-pill{
            width: 100%;
            justify-content: center;
          }

          /* Make toolbar fully stack */
          .toolbar{
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
          }

          .filters{
            width: 100%;
            flex-direction: column;
            align-items: stretch;
          }

          .actions{
            width: 100%;
            flex-direction: column;
            align-items: stretch;
            justify-content: flex-start;
            gap: 10px;
          }

          /* Controls full width (Week/Department/Staff/Search) */
          .control{
            width: 100%;
            justify-content: space-between;
          }
          .control label{
            width: 100%;
            justify-content: space-between;
          }

          /* Inputs + selects should not overflow */
          .control select,
          .control input[type="text"],
          .control input[type="date"]{
            width: 100%;
            max-width: 100%;
          }

          /* Your staff search inline style width:180 can cause squeeze,
            so enforce full width on mobile */
          .control input[type="text"]{
            width: 100% !important;
          }

          /* Meta row stacks */
          .meta-row{
            flex-direction: column;
            align-items: flex-start;
            gap: 6px;
          }

          /* Grid card header wraps */
          .grid-head{
            flex-wrap: wrap;
            gap: 10px;
          }

          /* Table MUST be horizontally scrollable on mobile */
          .table-wrap{
            width: 100%;
            overflow-x: auto;
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
          }

          /* Slightly smaller table so less scrolling */
          table{
            min-width: 920px;
          }
          .day-cell{
            min-width: 160px;
          }
          .staff-card{
            min-width: 210px;
          }

          /* Reduce cell height a bit */
          .cell-box{
            height: 150px;
          }
        }
        @media (max-width: 640px){
        /* ✅ BIG FIX: sticky staff column hides Sun/Mon on small screens */
        th.sticky-col,
        td.sticky-col{
          position: static !important;
          left: auto !important;
          z-index: auto !important;
          background: #fff !important;
          border-right: 1px solid #eef2f7;
        }

        /* ✅ also disable sticky header on mobile (prevents weird overlap) */
        thead th{
          position: static !important;
          top: auto !important;
          z-index: auto !important;
        }

        /* keep horizontal scroll working smoothly */
        .table-wrap{
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
      }
      `}</style>

      {confirmBox && (
        <div className="confirm-overlay" onMouseDown={() => confirmBox.onCancel()}>
          <div className="confirm-modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="confirm-head">
              <h4 className="confirm-title">{confirmBox.title}</h4>
              <button className="x-btn" onClick={() => confirmBox.onCancel()} aria-label="Close">
                ×
              </button>
            </div>
            <div className="confirm-body">{confirmBox.message}</div>
            <div className="confirm-actions">
              <button className="btn" type="button" onClick={() => confirmBox.onCancel()}>
                {confirmBox.cancelText || "Cancel"}
              </button>
              <button
                className={`btn ${confirmBox.danger ? "btn-danger-soft" : "btn-primary"}`}
                type="button"
                onClick={() => confirmBox.onOk()}
              >
                {confirmBox.okText || "OK"}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`toast ${toast.type || "info"}`} onClick={() => setToast(null)} title="Click to dismiss">
          <span className="mini-dot" />
          <div>{toast.message}</div>
        </div>
      )}

      <div className="header">
        <div>
          <h1>Admin Schedule Editor</h1>
          <p className="sub">Edit staff schedule for each week and department.</p>
        </div>

        <div className="status-pill" title={isPublished ? "Published (locked)" : "Draft (editable)"}>
          <span className="dot-mini" />
          {isPublished ? "Published / locked" : "Draft / editable"}
        </div>
      </div>

      <div className="list-card">
        <div className="toolbar">
          <div className="filters">
            <div className="control">
              <label>
                Week
                <span className="select-wrap">
                  <select
                    value={weekSelectValue}
                    onChange={async (e) => {
                      const v = e.target.value;

                      if (v === "range") {
                        setShowCustomWeek(true);
                        return;
                      }
                      if (v === "smart_prev") {
                        const id = await findOrCreateWeekByRange(smartWeeks.prev.start, smartWeeks.prev.end);
                        await refreshWeeks(id);
                        return;
                      }
                      if (v === "smart_cur") {
                        const id = await findOrCreateWeekByRange(smartWeeks.cur.start, smartWeeks.cur.end);
                        await refreshWeeks(id);
                        return;
                      }
                      if (v === "smart_next") {
                        const id = await findOrCreateWeekByRange(smartWeeks.next.start, smartWeeks.next.end);
                        await refreshWeeks(id);
                        return;
                      }

                      const id = Number(v);
                      setSelectedWeekId(id);
                      localStorage.setItem("last_week_id", String(id));
                    }}
                  >
                    <optgroup label="Timeframe">
                      <option value="smart_prev">
                        Previous week ({smartWeeks.prev.start} – {smartWeeks.prev.end})
                      </option>
                      <option value="smart_cur">
                        Current week ({smartWeeks.cur.start} – {smartWeeks.cur.end})
                      </option>
                      <option value="smart_next">
                        Next week ({smartWeeks.next.start} – {smartWeeks.next.end})
                      </option>
                    </optgroup>

                    <optgroup label="Custom">
                      <option value="range">Select range…</option>
                    </optgroup>
                  </select>
                </span>
              </label>
            </div>

            <div className="control">
              <label>
                Department
                <select
                  value={selectedDepartmentId}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setSelectedDepartmentId(v);
                    localStorage.setItem("last_department_id", String(v));
                  }}
                  disabled={!departments.length}
                >
                  {!departments.length ? (
                    <option value="">No departments available</option>
                  ) : null}

                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="control" style={{ gap: 12, flexWrap: "wrap" }}>
              <label style={{ gap: 10 }}>
                Staff
                <select
                  value={selectedStaffId}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedStaffId(v);
                    localStorage.setItem("last_staff_id", String(v));
                  }}
                >
                  <option value="all">All staff</option>
                  {staffOptions.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </label>

              <input
                type="text"
                value={staffSearch}
                onChange={(e) => {
                  setStaffSearch(e.target.value);
                  localStorage.setItem("last_staff_search", e.target.value);
                }}
                placeholder="Search staff..."
                style={{ width: 180 }}
              />

              {hasActiveScheduleFilters && (
                <button
                  type="button"
                  className="clear-filter-btn"
                  onClick={clearScheduleFilters}
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          <div className="actions">
            {isPublished ? (
              <div className="locked-hint">
                🔒 Locked (Published). Click <b>Unpublish</b> to edit.
              </div>
            ) : null}

            <button className="btn btn-primary" type="button" onClick={handleCreateNextWeekAuto}>
              Next week
            </button>
            <button className="btn btn-ghost" type="button" onClick={() => setShowCustomWeek((v) => !v)}>
              New week (custom)
            </button>
            <button className="btn" type="button" onClick={handleCloneWeek}>
              Clone (pick dates)
            </button>
            <button className="btn" type="button" onClick={handleCopyPreviousWeek} disabled={isPublished}>
              Copy previous week
            </button>
            <button
              className={`btn ${isPublished ? "btn-ghost" : "btn-danger"}`}
              type="button"
              onClick={handleTogglePublishWeek}
            >
              {isPublished ? "Unpublish" : "Publish"}
            </button>

            <button className="btn" type="button" onClick={() => loadSchedule()}>
              Refresh
            </button>

            <button className="btn" type="button" onClick={() => setBulkOpen((v) => !v)} disabled={isPublished}>
              Bulk actions
            </button>
          </div>
        </div>

        {showCustomWeek && (
          <div style={{ marginTop: 12, padding: 12, border: "1px solid #eef2f7", borderRadius: 16, background: "#f8fafc" }}>
            <div style={{ fontWeight: 900, marginBottom: 8 }}>Create custom week</div>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <div className="control">
                <label>
                  Start
                  <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
                </label>
              </div>
              <div className="control">
                <label>
                  End
                  <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
                </label>
              </div>
              <button className="btn btn-primary" type="button" onClick={handleCreateCustomWeek}>
                Create
              </button>
              <button className="btn" type="button" onClick={() => setShowCustomWeek(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {bulkOpen && (
          <div className="bulk-panel">
            <span className="bulk-title">Bulk</span>

            <div className="control">
              <label>
                Scope
                <select value={bulkScope} onChange={(e) => setBulkScope(e.target.value)}>
                  <option value="filtered">Filtered staff</option>
                  <option value="selected">Selected staff only</option>
                </select>
              </label>
            </div>

            {bulkScope === "selected" && (
              <span className="bulk-mini">
                Selected: <b style={{ color: "#0f172a" }}>{bulkSelectedStaffIds.length}</b>{" "}
                <button className="btn" type="button" onClick={() => toggleAllVisible(true)} disabled={bulkRunning}>
                  Select visible
                </button>{" "}
                <button className="btn" type="button" onClick={clearBulkSelected} disabled={bulkRunning}>
                  Clear
                </button>
              </span>
            )}

            <div className="control">
              <label>
                Mode
                <select value={bulkMode} onChange={(e) => setBulkMode(e.target.value)}>
                  <option value="copy">Copy day → other days</option>
                  <option value="set_type">Set duty type</option>
                </select>
              </label>
            </div>

            {bulkMode === "copy" ? (
              <div className="control">
                <label>
                  Copy from
                  <select value={bulkSourceDay} onChange={(e) => setBulkSourceDay(Number(e.target.value))}>
                    {DAYS.map((d, idx) => (
                      <option key={d} value={idx}>
                        {d}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            ) : (
              <div className="control">
                <label>Set type</label>

                <div className="select-wrap">
                  <select className="nice-select" value={bulkSetType} onChange={(e) => setBulkSetType(e.target.value)}>
                    {DUTY_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div className="bulk-days">
              {DAYS.map((d, idx) => (
                <label key={d} className="chk">
                  <input
                    type="checkbox"
                    checked={bulkTargets.includes(idx)}
                    onChange={(e) => {
                      setBulkTargets((prev) => {
                        if (e.target.checked) return Array.from(new Set([...prev, idx])).sort((a, b) => a - b);
                        return prev.filter((x) => x !== idx);
                      });
                    }}
                  />
                  {d}
                </label>
              ))}
            </div>

            <button className="btn btn-primary" type="button" onClick={bulkApply} disabled={bulkRunning || isPublished}>
              {bulkRunning ? "Applying..." : "Apply"}
            </button>
            <button className="btn" type="button" onClick={() => setBulkOpen(false)} disabled={bulkRunning}>
              Close
            </button>
          </div>
        )}

        <div className="meta-row">
          <div>
            Showing{" "}
            <b>
              week {selectedWeekId}
              {currentWeek ? ` (${currentWeek.title})` : ""}
            </b>{" "}
            • Department <b>{departments.find((d) => d.id === selectedDepartmentId)?.name || selectedDepartmentId}</b>
          </div>

          <div>
            Rows: <b>{filteredUsers.length}</b> • Staff: <b>{
            selectedStaffId === "all"
              ? "All"
              : users.find((u) => String(u.id) === String(selectedStaffId))?.name || selectedStaffId
          }</b>
          </div>
        </div>
      </div>

      <div className="grid-card">
        <div className="grid-head">
          <h3>Schedule Grid</h3>
          <div className="status-pill" style={{ boxShadow: "none" }}>
            <span className="dot-mini" />
            {isPublished ? "Editing disabled" : "Editing enabled"}
          </div>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th className="sticky-col">
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <input
                      className="rowchk"
                      type="checkbox"
                      checked={allVisibleChecked}
                      ref={(el) => {
                        if (el) el.indeterminate = someVisibleChecked;
                      }}
                      onChange={(e) => toggleAllVisible(e.target.checked)}
                      title="Select all visible staff (for bulk actions)"
                      disabled={filteredUsers.length === 0}
                    />
                    Staff
                  </div>
                </th>
                {DAYS.map((d) => (
                  <th key={d}>{d}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {filteredUsers.map((u) => (
                <tr key={u.id}>
                  <td className="sticky-col">
                    <div className="staff-card">
                      <input
                        className="rowchk"
                        type="checkbox"
                        checked={bulkSelectedStaffIds.includes(u.id)}
                        onChange={(e) => toggleBulkStaff(u.id, e.target.checked)}
                        title="Select this staff for bulk actions"
                      />
                      <div className="staff-info">
                        <div className="staff-name">{u.name}</div>
                        <div className="staff-sub">ID: {u.id}</div>
                      </div>
                    </div>
                  </td>

                  {DAYS.map((_, dayIndex) => {
                    const entry = u.entriesByDay[dayIndex];
                    const duty = entry?.duty_type || "DAY_OFF";
                    const dtKey = normalizeDutyTypeKey(duty);
                    const canEditDetails = dtKey === "SHIFT" || dtKey === "TRAINING";

                    const locForCell = getEntryDutyLocation(entry);

                    // ✅ use department.color based on location name (FLOAT / GATEKEEPER etc.)
                    const deptAccent = getDeptColorByLocationName(departments, locForCell);
                    const accent = deptAccent || "#64748b";

                    const cellStyle = isOffType(dtKey)
                      ? { background: "#ffffff" }
                      : locForCell
                      ? {
                          background: `linear-gradient(180deg, ${hexToRgba(accent, 0.12)}, #ffffff)`,
                          borderColor: `${hexToRgba(accent, 0.20)}`,
                        }
                      : { background: "#ffffff" };

                    return (
                      <td key={dayIndex} className="day-cell">
                        <div className="cell-box" style={cellStyle}>
                          <div className="cell-top">
                            <Select
                              options={DUTY_TYPES}
                              value={optionFromValue(duty)}
                              onChange={(opt) => handleQuickDutyTypeChange(entry, opt?.value)}
                              isDisabled={isPublished}
                              styles={selectStyles}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                            />
                          </div>

                          <div className="cell-body">{renderCellSummary(entry)}</div>

                          <div className="cell-footer">
                            {canEditDetails ? (
                              <button
                                type="button"
                                className="small-btn"
                                onClick={() => handleOpenEdit(u.id, dayIndex)}
                                disabled={isPublished}
                              >
                                Edit details
                              </button>
                            ) : (
                              <span style={{ fontSize: 12, color: "#94a3b8" }}>—</span>
                            )}
                          </div>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}

              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign: "center", padding: 18, color: "#64748b" }}>
                    No rows found for this filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editingCell && (
        <div className="modal-overlay" onMouseDown={() => setEditingCell(null)}>
          <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h4>
                Edit details — {users.find((x) => x.id === editingCell.userId)?.name || "Staff"} ({DAYS[editingCell.dayIndex]})
              </h4>
              <button className="x-btn" onClick={() => setEditingCell(null)} aria-label="Close">
                ×
              </button>
            </div>

            <form onSubmit={handleSaveEdit}>
              <div className="modal-body">
                <div className="field">
                  <label>Duty type</label>
                  <Select
                    options={DUTY_TYPES}
                    value={optionFromValue(editForm.duty_type)}
                    onChange={(opt) => updateEditField("duty_type", opt?.value)}
                    isDisabled={isPublished}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>

                <div className="field">
                  <label>Duty location</label>
                  <select
                    value={editForm.duty_location}
                    onChange={(e) => handleDutyLocationChange(e.target.value)}
                    disabled={isPublished || isOffType(editForm.duty_type)}
                  >
                    <option value="">-- choose location --</option>

                    {Array.isArray(departments) && departments.length > 0 && (
                      <optgroup label="All Departments">
                        {departments.map((d) => (
                          <option key={`dep_${d.id}`} value={d.name}>
                            🏷 {d.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>

                <div className="field">
                  <label>Start time</label>
                  <input
                    type="time"
                    value={editForm.start_time}
                    onChange={(e) => updateEditField("start_time", e.target.value)}
                    disabled={isPublished || isOffType(editForm.duty_type)}
                  />
                </div>

                <div className="field">
                  <label>End time</label>
                  <input
                    type="time"
                    value={editForm.end_time}
                    onChange={(e) => updateEditField("end_time", e.target.value)}
                    disabled={isPublished || isOffType(editForm.duty_type)}
                  />
                </div>

                <div className="field">
                  <label>Break start</label>
                  <input
                    type="time"
                    value={editForm.break_start}
                    onChange={(e) => updateEditField("break_start", e.target.value)}
                    disabled={isPublished || isOffType(editForm.duty_type)}
                  />
                </div>

                <div className="field">
                  <label>Break end</label>
                  <input
                    type="time"
                    value={editForm.break_end}
                    onChange={(e) => updateEditField("break_end", e.target.value)}
                    disabled={isPublished || isOffType(editForm.duty_type)}
                  />
                </div>

                <div className="field span2">
                  <label>Sub-duty / role</label>
                  <select
                    value={editForm.subDuty}
                    onChange={(e) => handleSubDutyChange(e.target.value)}
                    disabled={isPublished || currentSubduties.length === 0 || isOffType(editForm.duty_type)}
                  >
                    <option value="">
                      {currentSubduties.length === 0 ? "No predefined sub-duties for this location" : "-- choose sub-duty --"}
                    </option>
                    {currentSubduties.map((sd) => (
                      <option key={sd.id} value={sd.name}>
                        {sd.name}
                      </option>
                    ))}
                  </select>

                  {subdutiesError && (
                    <p style={{ color: "red", fontSize: "12px", margin: "6px 0 0" }}>{subdutiesError}</p>
                  )}
                </div>

                <div className="field span2">
                  <label>Note</label>
                  <input
                    type="text"
                    value={editForm.note}
                    onChange={(e) => updateEditField("note", e.target.value)}
                    disabled={isPublished}
                    placeholder="Optional note"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn" type="button" onClick={() => setEditingCell(null)}>
                  Close
                </button>
                <button className="btn btn-primary" type="submit" disabled={!editForm.id || isPublished}>
                  Save changes
                </button>
              </div>

              {isPublished && (
                <div style={{ padding: "0 16px 14px", color: "#b45309", fontSize: 12 }}>
                  This week is published (locked). Click <b>Unpublish</b> to edit.
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminScheduleEditorPage;